import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const RECOMMENDED_FILTERS_QUERY_KEY = 'filters';

/** When set to `1`, browse requests use `is_filtered` (PLP `/filter/` listings). */
export const RECOMMENDED_LISTING_FILTERED_QUERY_KEY = 'filtered';

/** Legacy keys — removed when writing new `filters` state. */
const LEGACY_BROWSE_TAGS_KEY = 'browseTags';
const LEGACY_BROWSE_FILTERED_KEY = 'browseFiltered';

/** Spaces in a tag → `+` in the URL segment (PLP-style). */
export function tagToUrlSegment(tag: string): string {
  return tag.trim().replace(/\s+/g, '+');
}

function normaliseFiltersForBrowse(raw: string): string {
  return raw.trim().replace(/ /g, '+');
}

/**
 * Build `filters` query / browse `tags` value.
 * Hyphen between segments; used for chip selection (single segment) or full facet string.
 */
export function buildFiltersParamValue(canonicalTags: string[]): string {
  const sorted = [...new Set(canonicalTags.map((t) => t.trim()).filter(Boolean))].sort();
  return sorted.map(tagToUrlSegment).join('-');
}

/**
 * Parse `filters` using greedy longest-match against allowed canonical tags
 * so tags may contain hyphens (e.g. `rose-gold`) without breaking on `-`.
 */
export function parseFiltersParam(raw: string, allowedCanonicalTags: string[]): string[] {
  const allowed = [...new Set(allowedCanonicalTags.map((t) => t.trim()).filter(Boolean))];
  if (!raw.trim() || allowed.length === 0) return [];

  const encodings = allowed
    .map((canon) => ({
      canon,
      seg: tagToUrlSegment(canon),
    }))
    .sort((a, b) => b.seg.length - a.seg.length);

  let s = raw.trim();
  const out: string[] = [];

  while (s.length > 0) {
    s = s.replace(/^-+/, '');
    if (!s) break;

    let matched = false;
    for (const { canon, seg } of encodings) {
      const spaced = seg.replace(/\+/g, ' ');
      if (s.startsWith(seg) && (s.length === seg.length || s[seg.length] === '-')) {
        out.push(canon);
        s = s.slice(seg.length);
        matched = true;
        break;
      }
      if (s.startsWith(spaced) && (s.length === spaced.length || s[spaced.length] === '-')) {
        out.push(canon);
        s = s.slice(spaced.length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      const idx = s.indexOf('-');
      if (idx === -1) break;
      s = s.slice(idx + 1);
    }
  }

  return out;
}

/** Which category chip is active when `filters` includes facet suffixes (e.g. `earrings-women`). */
export function inferMainCategoryTag(filtersRaw: string, allowedTags: string[]): string | null {
  const raw = filtersRaw.trim();
  if (!raw) return null;
  const allowed = [...new Set(allowedTags.map((t) => t.trim()).filter(Boolean))];
  if (allowed.length === 0) return null;

  const parsed = parseFiltersParam(raw, allowedTags);
  const allowedSet = new Set(allowed);
  const fromParse = parsed.find((t) => allowedSet.has(t));
  if (fromParse) return fromParse;

  const encodings = allowed
    .map((canon) => ({ canon, seg: tagToUrlSegment(canon) }))
    .sort((a, b) => b.seg.length - a.seg.length);

  for (const { canon, seg } of encodings) {
    if (raw === seg || raw.startsWith(`${seg}-`)) return canon;
    const spaced = seg.replace(/\+/g, ' ');
    if (raw === spaced || raw.startsWith(`${spaced}-`)) return canon;
  }
  return null;
}

export interface UseRecommendedBrowseUrlFiltersOptions {
  allowedTags: string[];
  /** When no `filters` query (or empty), passed to browse as `tags`. */
  defaultBrowseTags?: string;
}

export interface UseRecommendedBrowseUrlFiltersResult {
  /** Main category chip, inferred from start of `filters` (null = All). */
  selectedTag: string | null;
  /** Full `filters` query value, normalized for browse API (or default when All). */
  listingTagString: string;
  /** Browse `is_filtered` from `filtered=1`. */
  listingIsFiltered: boolean;
  selectTag: (canonicalTag: string) => void;
  clearAll: () => void;
  /** Apply PLP apply/reset URL as a single `filters` string (+ optional filtered flag). */
  setFiltersFromBrowseCommit: (tags: string | null, isFiltered: boolean) => void;
  isTagSelected: (canonicalTag: string) => boolean;
  isAllSelected: boolean;
}

function stripLegacyBrowseParams(next: URLSearchParams) {
  next.delete(LEGACY_BROWSE_TAGS_KEY);
  next.delete(LEGACY_BROWSE_FILTERED_KEY);
}

export function useRecommendedBrowseUrlFilters({
  allowedTags,
  defaultBrowseTags = '',
}: UseRecommendedBrowseUrlFiltersOptions): UseRecommendedBrowseUrlFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const allowedSet = useMemo(() => new Set(allowedTags.map((t) => t.trim()).filter(Boolean)), [allowedTags]);

  const filtersRaw = useMemo(
    () => searchParams.get(RECOMMENDED_FILTERS_QUERY_KEY)?.trim() ?? '',
    [searchParams]
  );

  const selectedTag = useMemo(
    () => inferMainCategoryTag(filtersRaw, allowedTags),
    [filtersRaw, allowedTags]
  );

  const listingTagString = useMemo(() => {
    if (!filtersRaw) return defaultBrowseTags ?? '';
    return normaliseFiltersForBrowse(filtersRaw);
  }, [filtersRaw, defaultBrowseTags]);

  const listingIsFiltered = useMemo(
    () => searchParams.get(RECOMMENDED_LISTING_FILTERED_QUERY_KEY) === '1',
    [searchParams]
  );

  const setFiltersParam = useCallback(
    (canonicalTags: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          stripLegacyBrowseParams(next);
          const sorted = [...new Set(canonicalTags.map((t) => t.trim()).filter(Boolean))].sort();
          if (sorted.length === 0) {
            next.delete(RECOMMENDED_FILTERS_QUERY_KEY);
            next.delete(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
          } else {
            next.set(RECOMMENDED_FILTERS_QUERY_KEY, buildFiltersParamValue(sorted));
            next.delete(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const selectTag = useCallback(
    (canonicalTag: string) => {
      const trimmed = canonicalTag.trim();
      if (!trimmed || !allowedSet.has(trimmed)) return;
      const nextVal = buildFiltersParamValue([trimmed]);
      const filtered = searchParams.get(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
      if (filtersRaw === nextVal && !filtered) return;
      setFiltersParam([trimmed]);
    },
    [allowedSet, filtersRaw, searchParams, setFiltersParam]
  );

  const clearAll = useCallback(() => {
    const hasFiltered = searchParams.get(RECOMMENDED_LISTING_FILTERED_QUERY_KEY) === '1';
    if (!filtersRaw && !hasFiltered) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        stripLegacyBrowseParams(next);
        next.delete(RECOMMENDED_FILTERS_QUERY_KEY);
        next.delete(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
        return next;
      },
      { replace: false }
    );
  }, [filtersRaw, searchParams, setSearchParams]);

  const setFiltersFromBrowseCommit = useCallback(
    (tags: string | null, isFiltered: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          stripLegacyBrowseParams(next);
          if (tags == null || tags === '') {
            next.delete(RECOMMENDED_FILTERS_QUERY_KEY);
            next.delete(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
          } else {
            next.set(RECOMMENDED_FILTERS_QUERY_KEY, normaliseFiltersForBrowse(tags));
            if (isFiltered) next.set(RECOMMENDED_LISTING_FILTERED_QUERY_KEY, '1');
            else next.delete(RECOMMENDED_LISTING_FILTERED_QUERY_KEY);
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const isTagSelected = useCallback(
    (canonicalTag: string) => {
      const t = canonicalTag.trim();
      if (!t) return false;
      return selectedTag === t;
    },
    [selectedTag]
  );

  const isAllSelected = !filtersRaw;

  return {
    selectedTag,
    listingTagString,
    listingIsFiltered,
    selectTag,
    clearAll,
    setFiltersFromBrowseCommit,
    isTagSelected,
    isAllSelected,
  };
}
